import { Component, OnInit } from '@angular/core';
import { Models } from '@ibexfusion/models';
import { MatDialog } from '@angular/material/dialog';
import { ApplicationGenerationComponent } from '../../../components/application-generation/application-generation.component';
import { CustomerWizardService } from '@services/customer-wizards/customer-wizard.service';
import { ActivatedRoute, Router } from '@angular/router';
import { PageAction } from '../../../components/page-actions/page-actions.component';
import { HttpClient } from '@angular/common/http';
import { EnvironmentService } from '@services/environment/environment.service';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { ProfileService } from '@services/profiles/profile.service';
import { MessengerService } from '@services/messenger/messenger.service';
import { Services } from '@ibexfusion/core-utils';
import { OrganisationService } from '@services/organisations/organisation.service';
import { DialogService } from '@services/dialog/dialog.service';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-customer-wizard-edit',
    templateUrl: './customer-wizard-edit.component.html',
    styleUrls: ['./customer-wizard-edit.component.scss'],
    providers: [CustomerWizardService, ProfileService, OrganisationService],
    standalone: false
})
export class CustomerWizardEditComponent implements OnInit {
  public wizardData: Models.CustomerWizardData;
  isCreate = false;
  isLoading = true;
  pageActions = [];
  alreadyExistingProfiles = [];
  errors = [];

  smartInputFields: any = [
    { key: 'customerName', translationPrefix: 'customerWizard' }, { key: 'firstName', translationPrefix: 'customerWizard' },
    { key: 'lastName', translationPrefix: 'customerWizard' }, { key: 'street', translationPrefix: 'customerWizard' }, { key: 'zip', translationPrefix: 'customerWizard' },
    { key: 'city', translationPrefix: 'customerWizard' }, { key: 'countryCode', translationPrefix: 'customerWizard' }, { key: 'tel', translationPrefix: 'customerWizard' },
    { key: 'fax', translationPrefix: 'customerWizard' }, { key: 'url', translationPrefix: 'customerWizard' }, { key: 'mail', translationPrefix: 'customerWizard' },
    { key: 'languages', translationPrefix: 'customerWizard', change: () => { this.languageChange() } }, { key: 'latitude', translationPrefix: 'customerWizard' },
    { key: 'longitude', translationPrefix: 'customerWizard' }, { key: 'linkName', translationPrefix: 'customerWizard' },
    { key: 'hasSupport', translationPrefix: 'customerWizard', class: 'short' }, { key: 'isIbexsmart', translationPrefix: 'customerWizard', class: 'short' },
    { key: 'isIbextouch', translationPrefix: 'customerWizard', class: 'short' }, { key: 'isIbextablet', translationPrefix: 'customerWizard', class: 'short' },
    { key: 'category', translationPrefix: 'organisation.tag' }
  ]

  constructor(
    private dialog: MatDialog, public customerWizardService: CustomerWizardService, private route: ActivatedRoute, private router: Router, public http: HttpClient,
    private ms: MessengerService, private environmentService: EnvironmentService, private snackbar: SnackbarService, private profileService: ProfileService,
    private organisationService: OrganisationService, private dialogService: DialogService
  ) {
  }

  async ngOnInit() {
    this.isCreate = !this.route.snapshot.params['id'];
    this.wizardData = !!this.route.snapshot.params['id'] ? await this.customerWizardService.get(this.route.snapshot.params['id']) : new Models.CustomerWizardData();
    this.isLoading = false;
    if (!!this.route.snapshot.params['id']) {
      this.initializePageActions();
    }
  }

  initializePageActions() {
    this.pageActions = [
      new PageAction({ icon: 'save', title: 'Speichern', callback: () => { this.save(); } }),
      new PageAction({
        icon: this.wizardData?.result?.success ? 'delete' : 'rowing', title: this.wizardData?.result?.success ? 'Kunde wieder löschen' : 'Ausführen',
        callback: () => this.wizardData?.result?.success ? this.rollback() : this.execute()
      })];
  }

  addBackendUser() {
    this.wizardData.backendUsers.push(new Models.CustomerBackendUserModel);
  }

  addApplication() {
    const dialogRef = this.dialog.open(ApplicationGenerationComponent, { width: '75vw', data: { wizard: this.wizardData } });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.wizardData.apps.push(result);
      }
    });
  }

  editApplication(app, index) {
    const dialogRef = this.dialog.open(ApplicationGenerationComponent, { width: '75vw', data: { app: JSON.parse(JSON.stringify(app)) } });
    dialogRef.afterClosed().subscribe(result => {
      this.wizardData.apps[index] = !!result ? result : this.wizardData.apps[index];
    });
  }

  async save() {
    this.alreadyExistingProfiles = [];
    for (let i = 0; i < this.wizardData.backendUsers.length; i++) {
      const profile = (await this.profileService.find(0, 1, { email: this.wizardData.backendUsers[i].mail })).data[0];
      if (profile?.email) {
        this.alreadyExistingProfiles.push(profile.email);
      }
    }
    if (this.alreadyExistingProfiles.length > 0) {
      return;
    }
    if (this.isCreate) {
      const res = await this.customerWizardService.create(this.wizardData);
      this.router.navigate(['newCustomer', res._id, 'edit']);
    } else {
      this.customerWizardService.update(this.wizardData._id, this.wizardData);
    }
  }

  async execute() {
    if ((this.errors = this.checkData()).length || !(await this.dialogService.confirm('Organisationen anlegen? Sie werden danach auf das Dashboard der neu angelegten Organisation weitergeleitet.'))) return;

    this.isLoading = true;
    try {
      const res: any = await this.http.post(`${environment.apiBaseURL}/customerWizardService`, this.wizardData).toPromise();
      this.snackbar.success('Kunde wurde erfolgreich angelegt!', 5000);

      const org = await this.organisationService.get(Services.HashIdService.hashSingle(res?.data?.organisation));
      this.ms.setOrganisation(org as any);

      setTimeout(() => this.router.navigate([{ outlets: { org: org.name, primary: '' } }]).then(() =>
        this.router.navigate([{ outlets: { org: org.name, primary: 'dashboard' } }])
      ), 0);
    } catch (err) {
      this.snackbar.error(`Kunde konnte nicht angelegt werden: ${err}`, 5000);
      this.ngOnInit();
    }
  }

  checkData() {
    return ['customerName', 'languages', 'linkName'].filter(key =>
      !this.wizardData[key] || (Array.isArray(this.wizardData[key]) && !this.wizardData[key].length)
    );
  }

  rollback() {
    this.isLoading = true;
    return this.http.delete(environment.apiBaseURL + '/customerWizardService/' + this.wizardData._id).toPromise().then(res => {
      this.snackbar.success('Kunde wurde erfolgreich gelöscht!', 5000);
      this.ngOnInit();
    }).catch(err => {
      this.snackbar.error('Kunde konnte nicht gelöscht werden: ' + err, 5000);
      this.ngOnInit();
    });
  }

  languageChange() {
    for (let i = 0; i < this.wizardData.languages.length; i++) {
      this.wizardData.languages[i] = this.wizardData.languages[i].toLowerCase();
    }
  }
}
